import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
} from "@codemirror/view";
import { EditorState, Compartment, type Extension } from "@codemirror/state";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import {
  bracketMatching,
  defaultHighlightStyle,
  foldGutter,
  foldKeymap,
  indentUnit,
  syntaxHighlighting,
} from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { indentationMarkers } from "@replit/codemirror-indentation-markers";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { linter } from "@codemirror/lint";
import { jsLinter, pythonLinter } from "./linters";

export type EditorLanguage = "js" | "python";

export interface KarelEditor {
  getValue(): string;
  setValue(value: string): void;
  setReadOnly(readOnly: boolean): void;
  setLanguage(language: EditorLanguage): void;
  setLinting(enabled: boolean): void;
  focus(): void;
}

const themeCompartment = new Compartment();
const readOnlyCompartment = new Compartment();

export interface KarelCodeEditorOptions {
  readOnly?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  language?: EditorLanguage;
  linting?: boolean;
}

function languageSupport(language: EditorLanguage): Extension {
  return language === "python" ? python() : javascript();
}

function lintSupport(language: EditorLanguage, enabled: boolean): Extension {
  if (!enabled) return [];
  const source = language === "python" ? pythonLinter : jsLinter;
  return linter(source, { delay: 600 });
}

/**
 * Thin wrapper around CodeMirror 6 exposing the small API the rest of the app
 * needs. Replaces the legacy Ace editor.
 */
export class KarelCodeEditor implements KarelEditor {
  private view: EditorView;
  private language: EditorLanguage;
  private linting: boolean;
  private languageCompartment = new Compartment();
  onChange?: (value: string) => void;

  constructor(parent: HTMLElement, options: KarelCodeEditorOptions = {}) {
    this.onChange = options.onChange;
    this.language = options.language ?? "js";
    this.linting = options.linting ?? true;

    const extensions: Extension[] = [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      foldGutter(),
      bracketMatching(),
      indentationMarkers({
        highlightActiveBlock: false,
        thickness: 1,
        colors: {
          light: "rgba(148, 163, 184, 0.5)",
          dark: "rgba(148, 163, 184, 0.5)",
        },
      }),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      history(),
      EditorView.lineWrapping,
      EditorState.tabSize.of(4),
      indentUnit.of("\t"),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        indentWithTab,
      ]),
      this.languageCompartment.of([
        languageSupport(this.language),
        lintSupport(this.language, this.linting),
      ]),
      themeCompartment.of([]),
      readOnlyCompartment.of([
        EditorState.readOnly.of(!!options.readOnly),
        EditorView.editable.of(!options.readOnly),
      ]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          this.onChange?.(this.getValue());
        }
      }),
    ];

    this.view = new EditorView({
      doc: options.value ?? "",
      extensions,
      parent,
    });
  }

  getValue(): string {
    return this.view.state.doc.toString();
  }

  setValue(value: string): void {
    this.view.dispatch({
      changes: { from: 0, to: this.view.state.doc.length, insert: value },
    });
  }

  setReadOnly(readOnly: boolean): void {
    this.view.dispatch({
      effects: readOnlyCompartment.reconfigure([
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
      ]),
    });
  }

  setLanguage(language: EditorLanguage): void {
    if (language === this.language) return;
    this.language = language;
    this.view.dispatch({
      effects: this.languageCompartment.reconfigure([
        languageSupport(language),
        lintSupport(language, this.linting),
      ]),
    });
  }

  setLinting(enabled: boolean): void {
    if (enabled === this.linting) return;
    this.linting = enabled;
    this.view.dispatch({
      effects: this.languageCompartment.reconfigure([
        languageSupport(this.language),
        lintSupport(this.language, enabled),
      ]),
    });
  }

  focus(): void {
    this.view.focus();
  }

  applyTheme(theme: "light" | "dark"): void {
    this.view.dispatch({
      effects: themeCompartment.reconfigure(theme === "dark" ? oneDark : []),
    });
  }

  destroy(): void {
    this.view.destroy();
  }
}
